import { EMPTY_ACCESS_REVIEW_HORIZON, type AccessReviewHorizon } from '@/userManagement/domain/entities/AccessReviewHorizon';
import type { AccessReviewSummary } from '@/userManagement/domain/entities/AccessReviewSummary';
import type { AccessReviewUser } from '@/userManagement/domain/entities/AccessReviewUser';
import type { PermissionDeviation, PermissionField } from '@/userManagement/domain/entities/Permission';
import { PERMISSION_FIELDS } from '@/userManagement/domain/entities/Permission';
import type { AccessReviewTableData } from '@/userManagement/application/types/AccessReviewTableData';

export interface AccessReviewDeviationWire {
  featureId?: number;
  FeatureId?: number;
  featureName?: string;
  FeatureName?: string;
  category?: string;
  Category?: string;
  field?: string;
  Field?: string;
  direction?: string;
  Direction?: string;
}

export interface AccessReviewUserWire {
  username?: string;
  Username?: string;
  empId?: number | null;
  EmpId?: number | null;
  customId?: string | null;
  CustomId?: string | null;
  fullName?: string;
  FullName?: string;
  roleName?: string;
  RoleName?: string;
  databaseNames?: string[];
  DatabaseNames?: string[];
  extraCount?: number;
  ExtraCount?: number;
  missingCount?: number;
  MissingCount?: number;
  totalDeviationCount?: number;
  TotalDeviationCount?: number;
  deviations?: AccessReviewDeviationWire[];
  Deviations?: AccessReviewDeviationWire[];
}

export interface AccessReviewSummaryWire {
  usersReviewed?: number;
  UsersReviewed?: number;
  deviantCount?: number;
  DeviantCount?: number;
  totalDeviations?: number;
  TotalDeviations?: number;
}

export interface AccessReviewListWire {
  items?: AccessReviewUserWire[];
  Items?: AccessReviewUserWire[];
  totalCount?: number;
  TotalCount?: number;
  pageNumber?: number;
  PageNumber?: number;
  pageSize?: number;
  PageSize?: number;
  summary?: AccessReviewSummaryWire;
  Summary?: AccessReviewSummaryWire;
  horizon?: AccessReviewHorizonWire;
  Horizon?: AccessReviewHorizonWire;
}

export interface AccessReviewHorizonWire {
  mode?: string;
  Mode?: string;
  permissionSnapshotId?: number | null;
  PermissionSnapshotId?: number | null;
  snapshotLabel?: string | null;
  SnapshotLabel?: string | null;
  snapshotUtcSavedOn?: string | null;
  SnapshotUtcSavedOn?: string | null;
  snapshotAsOfDate?: string | null;
  SnapshotAsOfDate?: string | null;
  message?: string | null;
  Message?: string | null;
}

const FIELD_ALIASES: Record<string, PermissionField> = {
  allowread: 'allowRead',
  allowwrite: 'allowWrite',
  allowdelete: 'allowDelete',
  allowcentralaccess: 'allowCentralAccess',
  allowexcelexport: 'allowExcelExport'
};

export function mapAccessReviewPermissionField(raw: string | null | undefined): PermissionField | null {
  if (raw == null) return null;
  const key = raw.trim();
  if ((PERMISSION_FIELDS as readonly string[]).includes(key)) {
    return key as PermissionField;
  }
  return FIELD_ALIASES[key.toLowerCase()] ?? null;
}

export function mapAccessReviewDeviationWire(dto: AccessReviewDeviationWire): PermissionDeviation | null {
  const field = mapAccessReviewPermissionField(dto.field ?? dto.Field);
  if (field == null) return null;

  const directionRaw = String(dto.direction ?? dto.Direction ?? '')
    .trim()
    .toLowerCase();
  if (directionRaw !== 'above' && directionRaw !== 'below') return null;

  return {
    featureId: Number(dto.featureId ?? dto.FeatureId ?? 0),
    featureName: String(dto.featureName ?? dto.FeatureName ?? '').trim(),
    category: String(dto.category ?? dto.Category ?? '').trim(),
    field,
    direction: directionRaw
  };
}

export function mapAccessReviewUserWire(dto: AccessReviewUserWire): AccessReviewUser {
  const username = String(dto.username ?? dto.Username ?? '').trim();
  const deviationsRaw = dto.deviations ?? dto.Deviations ?? [];
  const deviations = deviationsRaw.map(mapAccessReviewDeviationWire).filter((d): d is PermissionDeviation => d != null);

  const empRaw = dto.empId ?? dto.EmpId;
  const empId = typeof empRaw === 'number' && Number.isFinite(empRaw) ? empRaw : null;
  const customRaw = dto.customId ?? dto.CustomId;
  const customId = typeof customRaw === 'string' && customRaw.trim() !== '' ? customRaw.trim() : null;

  return {
    username,
    empId,
    customId,
    fullName: String(dto.fullName ?? dto.FullName ?? '').trim(),
    roleName: String(dto.roleName ?? dto.RoleName ?? '').trim(),
    databaseNames: (dto.databaseNames ?? dto.DatabaseNames ?? []).map((d) => String(d).trim()).filter(Boolean),
    extraCount: Number(dto.extraCount ?? dto.ExtraCount ?? 0),
    missingCount: Number(dto.missingCount ?? dto.MissingCount ?? 0),
    totalDeviationCount: Number(dto.totalDeviationCount ?? dto.TotalDeviationCount ?? deviations.length),
    deviations
  };
}

export function mapAccessReviewSummaryWire(dto: AccessReviewSummaryWire | null | undefined): AccessReviewSummary {
  if (dto == null) {
    return { usersReviewed: 0, deviantCount: 0, totalDeviations: 0 };
  }
  return {
    usersReviewed: Number(dto.usersReviewed ?? dto.UsersReviewed ?? 0),
    deviantCount: Number(dto.deviantCount ?? dto.DeviantCount ?? 0),
    totalDeviations: Number(dto.totalDeviations ?? dto.TotalDeviations ?? 0)
  };
}

export function mapAccessReviewHorizonWire(dto: AccessReviewHorizonWire | null | undefined): AccessReviewHorizon {
  if (dto == null) {
    return { ...EMPTY_ACCESS_REVIEW_HORIZON };
  }

  const modeRaw = String(dto.mode ?? dto.Mode ?? 'live')
    .trim()
    .toLowerCase();
  const mode: AccessReviewHorizon['mode'] = modeRaw === 'snapshot' || modeRaw === 'unresolved' ? modeRaw : 'live';

  const snapshotIdRaw = dto.permissionSnapshotId ?? dto.PermissionSnapshotId;
  const snapshotId = typeof snapshotIdRaw === 'number' && Number.isFinite(snapshotIdRaw) ? snapshotIdRaw : null;

  const label = dto.snapshotLabel ?? dto.SnapshotLabel;
  const saved = dto.snapshotUtcSavedOn ?? dto.SnapshotUtcSavedOn;
  const asOf = dto.snapshotAsOfDate ?? dto.SnapshotAsOfDate;
  const message = dto.message ?? dto.Message;

  return {
    mode,
    permissionSnapshotId: snapshotId,
    snapshotLabel: typeof label === 'string' && label.trim() !== '' ? label.trim() : null,
    snapshotUtcSavedOn: typeof saved === 'string' && saved.trim() !== '' ? saved : null,
    snapshotAsOfDate: typeof asOf === 'string' && asOf.trim() !== '' ? asOf : null,
    message: typeof message === 'string' && message.trim() !== '' ? message.trim() : null
  };
}

export function mapAccessReviewListWire(dto: AccessReviewListWire): {
  users: AccessReviewUser[];
  totalCount: number;
  summary: AccessReviewSummary;
  horizon: AccessReviewHorizon;
} {
  const items = dto.items ?? dto.Items ?? [];
  const users = items.map(mapAccessReviewUserWire).filter((u) => u.username !== '');
  return {
    users,
    totalCount: Number(dto.totalCount ?? dto.TotalCount ?? users.length),
    summary: mapAccessReviewSummaryWire(dto.summary ?? dto.Summary),
    horizon: mapAccessReviewHorizonWire(dto.horizon ?? dto.Horizon)
  };
}

export function mapAccessReviewUserToTableData(user: AccessReviewUser): AccessReviewTableData {
  return {
    ...user,
    id: user.username
  };
}
